import React from 'react'
import { useAppDispatch } from '../../../../hooks/redux'

import BaseModal from '../BaseModal2'
import Button from '../../../atoms/Button'
import Participant from '../../../atoms/Participant'
import AgentClientSearchToggle from '../../../atoms/AgentClientSearchToggle'
import SearchUser from '../../../molecules/ChatModals/SearchUser'

import { addParticipant, removeParticipant } from '../../../../lib/api'
import { arraysEqual, arrayDifference } from '../../../../utilities'
import {
  EXTERNAL,
  useAddRemoveParticipant
} from '../../../../providers/AddRemoveParticipantProvider'
import { useUserAPI } from '../../../../providers/UserAPIProvider'
import { addUsers, removeUsers } from '../../../../slices/convo'
import { addMembers, removeMembers } from '../../../../slices/dms'

const AddRemoveUserModal = ({ isDirect, convo, open, onClose, users }) => {
  const dispatch = useAppDispatch()
  const {
    search,
    setAddedUsers,
    editIDs,
    setEditSessionUsers,
    currentUserIDs,
    options
  } = useAddRemoveParticipant()
  const { allUsers, loadUser } = useUserAPI()

  const [foundUser, setFoundUser] = React.useState(null)
  const [disable, setDisable] = React.useState(true)

  React.useEffect(() => {
    if (foundUser) {
      if (!allUsers[foundUser.id]) {
        loadUser(foundUser.id)
      }
      setAddedUsers(u => [...u, foundUser])
    }
  }, [foundUser, setAddedUsers, allUsers, loadUser])

  React.useEffect(() => {
    if (arraysEqual(editIDs, currentUserIDs)) {
      setDisable(true)
    } else {
      setDisable(false)
    }
  }, [editIDs, currentUserIDs])

  const handleSessionUpdate = React.useCallback(async () => {
    try {
      // if edit id doesn't exist in current user ids, then add
      const toAdd = arrayDifference(editIDs, currentUserIDs)

      if (toAdd.length) {
        await Promise.all(
          toAdd.map(async id => {
            await addParticipant(convo.session, id)
          })
        )
        if (isDirect) {
          // Add to dms
          dispatch(addMembers({ id: convo.session, users: toAdd }))
        } else {
          // Add to socket
          dispatch(addUsers({ id: convo.session, users: toAdd }))
        }
      }

      // ids that exist in current user ids but not in edit ids need to be removed
      const toDelete = arrayDifference(currentUserIDs, editIDs)
      if (toDelete.length) {
        await Promise.all(
          toDelete.map(async id => {
            await removeParticipant(convo.session, id)
          })
        )
        if (isDirect) {
          // Remove from dms
          dispatch(removeMembers({ id: convo.session, users: toDelete }))
        } else {
          // Remove from socket
          dispatch(removeUsers({ id: convo.session, users: toDelete }))
        }
      }

      setEditSessionUsers(false)
    } catch (err) {
      console.error('Error updating or removing participant', err)
    }
  }, [editIDs, convo, setEditSessionUsers, currentUserIDs, dispatch, isDirect])

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title='ADD/REMOVE PARTICIPANTS'
      PaperProps={{ style: { top: '53%', width: '38%' } }}
    >
      <AgentClientSearchToggle />

      {options.length > 0 && (
        <SearchUser
          searchingGuests={search === EXTERNAL}
          noAdd
          peerChat
          foundUser={foundUser}
          setFoundUser={setFoundUser}
          search={search}
        />
      )}

      <Participant users={users} />

      <Button
        disabled={disable}
        onClick={() => handleSessionUpdate()}
        style={{ marginTop: '1rem' }}
      >
        SAVE
      </Button>
    </BaseModal>
  )
}

export default AddRemoveUserModal
